import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import config from '../config';
import { resolveTraitDependencies } from '../config/shuffleTraits';
import useTraitShuffleStore from '../hooks/useTraitShuffleStore';
import { fetchImage, handleFetchImage } from '../utils/canvas';
import { getTraitImagePath } from '../utils/traits';
import Spinner from './Spinner';

const DEFAULT_WIDTH = 3000;
const DEFAULT_HEIGHT = 3000;

const TraitShufflePreviewImage = ({ index }) => {
  const canvasRef = useRef(null);
  const { selectedNft, addFailedTraitImage, previewTrait, clearPreviewTrait } = useTraitShuffleStore();

  const getPropValue = useCallback(
    (property) => {
      // If we're previewing a trait, we need to resolve dependencies
      if (previewTrait && selectedNft?.metadata) {
        const currentTraits = { ...selectedNft.metadata.properties };

        // Apply the preview trait
        currentTraits[previewTrait.layer] = previewTrait.value;

        // Resolve dependencies - this will automatically set required traits
        const resolvedTraits = resolveTraitDependencies(currentTraits);

        // Return the resolved value for this property
        return resolvedTraits[property] || '';
      }

      // Otherwise use the original trait value
      const metadata = selectedNft?.metadata;
      if (!metadata) return '';
      if (!metadata.properties) return '';
      return metadata.properties[property] || '';
    },
    [selectedNft, previewTrait]
  );

  useEffect(() => {
    if (!selectedNft) return;
    if (!selectedNft.metadata) return;
    if (!selectedNft.metadata.properties) return;

    const images = config.fourthLitterTraitLayers.map((property) => {
      const propValue = getPropValue(property);
      const currentTraits = { ...selectedNft.metadata.properties };
      const updatedTraits = previewTrait?.value
        ? resolveTraitDependencies({ ...currentTraits, [previewTrait.layer]: previewTrait.value })
        : currentTraits;
      const imageUrl = getTraitImagePath(property, propValue, updatedTraits);

      return {
        src: imageUrl,
        x: 0,
        y: 0,
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
      };
    });

    const canvasContext = canvasRef.current?.getContext('2d');
    if (!canvasContext) return;

    Promise.all(images.map(fetchImage)).then((results) =>
      results.forEach((result) => {
        if (result.errorMsg) {
          addFailedTraitImage(result.image.src);
        }
        handleFetchImage(canvasContext, result);
      })
    );
  }, [selectedNft, getPropValue, addFailedTraitImage]);

  return (
    <div className={clsx('relative aspect-square w-full max-w-[750px] overflow-hidden rounded-lg bg-gray-900')}>
      <canvas ref={canvasRef} width={3000} height={3000} className='h-full w-full rounded-lg z-20 relative' />
      <div className='absolute left-0 top-0 z-10 w-full h-full bg-black/50 flex items-center justify-center'>
        <Spinner />
      </div>
      {previewTrait && (
        <div className='absolute top-4 left-4 z-30'>
          <div className='bg-black/80 backdrop-blur-sm rounded-lg px-3 py-2 text-white text-sm'>
            <div className='font-medium mb-1'>Previewing: {previewTrait.value}</div>
            <div className='text-xs text-gray-300'>
              Layer: {previewTrait.layer} • Rarity: {previewTrait.rarity}
            </div>
            <button onClick={clearPreviewTrait} className='mt-2 px-2 py-1 bg-gray-600 hover:bg-gray-500 rounded text-xs transition-colors'>
              Clear Preview
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const TraitShufflePreviewImages = () => {
  const { selectedNft } = useTraitShuffleStore();

  if (!selectedNft) {
    return (
      <div className='flex flex-col gap-4 sticky top-5'>
        <div className='aspect-square w-full max-w-[750px] overflow-hidden rounded-lg bg-gray-900 flex items-center justify-center'>
          <p className='text-gray-400'>Select a kitty to preview</p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4 sticky top-5'>
      <TraitShufflePreviewImage index={0} />
    </div>
  );
};

export default TraitShufflePreviewImages;
